import { Flex, Stack, Text } from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate } from 'react-router-dom'

import IconDisc from 'components/icon-disc'
import { createSizeStyleProps } from 'utils/style-utils'

import theme from 'theme'

import type { FlexProps, StackProps } from '@chakra-ui/react'
import type { IconDiscProps } from 'components/icon-disc'
import type { ReactNode } from 'react'
import type { ComponentSize } from 'types'

const styleProps = createSizeStyleProps<{
  container: Partial<FlexProps>
  icon: Partial<IconDiscProps>
  actions: Partial<StackProps>
}>({
  sm: {
    container: {
      p: '16px 32px',
      direction: 'row',
      align: 'center',
      _hover: {
        cursor: 'pointer',
      },
    },
    icon: {
      size: '40px',
      mr: '16px',
      mb: 0,
    },
    actions: {
      ml: 'auto',
    },
  },
  md: {
    container: {
      p: '16px 32px',
      direction: 'row',
      align: 'center',
      _hover: {
        cursor: 'pointer',
      },
    },
    icon: {
      size: '40px',
      mr: '16px',
      mb: 0,
    },
    actions: {
      ml: 'auto',
    },
  },
  lg: {
    container: {
      p: '16px 32px',
      direction: 'column',
      align: 'left',
      transition: 'all ease 0.2s',
      _hover: {
        transform: 'translateY(-4px)',
        transition: 'all ease 0.2s',
        boxShadow: theme.shadows.lg,
        cursor: 'pointer',
      },
    },
    icon: {
      size: '60px',
      mr: 0,
      mb: '16px',
    },
    actions: {
      ml: 0,
      mt: '32px',
    },
  },
})

type CardActionLinkProps = {
  url: string
  isExternal?: boolean
}

export type CardActionProps = FlexProps & {
  title: string
  message?: string
  messageComponent?: ReactNode
  link?: CardActionLinkProps
  icon?: IconDiscProps
  isComplete?: boolean
  isDisabled?: boolean
  size?: ComponentSize
}

const CardAction = ({
  icon,
  title,
  message,
  messageComponent,
  link,
  isComplete,
  isDisabled,
  size = 'sm',
  ...flexProps
}: CardActionProps) => {
  const navigate = useNavigate()

  const onClick = (link?: CardActionProps['link']) => {
    if (!link) return
    if (!link.isExternal) navigate(link.url)
    if (link.isExternal && link.url) window.open(link.url, '_blank')?.focus()
  }

  return (
    <Flex
      background={theme.background.card}
      align="center"
      borderRadius="base"
      boxShadow="base"
      opacity={theme.opacity[isDisabled ? 'disabled' : 'full']}
      userSelect="none"
      {...(onClick && !isDisabled && { onClick: () => onClick(link) })}
      {...flexProps}
      {...styleProps[size].container}
      {...(isDisabled && { _hover: { cursor: 'not-allowed' } })}
      {...(!flexProps.onClick && !link && { _hover: { cursor: 'auto' } })}
    >
      {icon && (
        <IconDisc
          source={icon.source}
          color={icon.color}
          background={icon.background ?? 'transparent'}
          {...styleProps[size].icon}
        />
      )}
      <Stack spacing="0" mr="16px">
        <Text textStyle="body1Bold">{title}</Text>
        <Text textStyle="body1" color={theme.baseColors.text.disabled}>
          {message}
        </Text>
        {messageComponent}
      </Stack>
      <Stack direction="row" spacing="16px" align="center" {...styleProps[size].actions}>
        {isComplete && <FontAwesomeIcon icon={['far', 'circle-check']} color={theme.colors.purple['600']} size="sm" />}
        {link && (
          <FontAwesomeIcon
            icon={['far', link.isExternal ? 'arrow-up-right-from-square' : 'chevron-right']}
            color={link.isExternal ? theme.baseColors.primary.main : theme.baseColors.text.disabled}
          />
        )}
      </Stack>
    </Flex>
  )
}

export default CardAction
